import { createContext, useContext, type ReactNode } from "react";
import type { ApiClientInterface } from "@tabipla/hooks/infrastructure";
import { apiClient } from "./client";

// ==================== Context ====================

const ApiClientContext = createContext<ApiClientInterface | null>(null);

// ==================== Provider ====================

interface ApiClientProviderProps {
  children: ReactNode;
  client?: ApiClientInterface;
}

/**
 * ApiClientProvider - Provides the API client to all child components.
 *
 * Usage:
 * - Production: Use without client prop to use the default apiClient
 * - Testing: Pass a mock client for testing purposes
 *
 * @example
 * // Production (in App.tsx)
 * <ApiClientProvider>
 *   <App />
 * </ApiClientProvider>
 *
 * @example
 * // Testing
 * const mockClient = { get: vi.fn(), post: vi.fn(), ... };
 * <ApiClientProvider client={mockClient}>
 *   <ComponentUnderTest />
 * </ApiClientProvider>
 */
export function ApiClientProvider({ children, client }: ApiClientProviderProps) {
  // Use provided client or fall back to default apiClient
  const value = client ?? apiClient;

  return <ApiClientContext.Provider value={value}>{children}</ApiClientContext.Provider>;
}

// ==================== Hook ====================

/**
 * useApiClient - Access the API client from context.
 *
 * @throws Error if used outside of ApiClientProvider
 * @returns ApiClientInterface
 *
 * @example
 * function useMyData() {
 *   const api = useApiClient();
 *   return useQuery({
 *     queryKey: ["myData"],
 *     queryFn: () => api.get("/my-endpoint"),
 *   });
 * }
 */
export function useApiClient(): ApiClientInterface {
  const context = useContext(ApiClientContext);
  if (!context) {
    throw new Error("useApiClient must be used within an ApiClientProvider");
  }
  return context;
}

// ==================== Direct Access (for migration) ====================

/**
 * getApiClient - Get the API client directly (for use outside React components).
 *
 * NOTE: Prefer useApiClient() hook when inside React components.
 * This function is provided for:
 * - Gradual migration from direct imports
 * - Use in non-React code (rare cases)
 *
 * @returns ApiClientInterface (the default apiClient singleton)
 */
export function getApiClient(): ApiClientInterface {
  return apiClient;
}
